import { useCallback, useEffect, useRef, useState } from 'react'
import { io } from 'socket.io-client'
import api from '../services/api'

export interface ExtensionSessionInfo {
  /**
   * Rollup across every connected machine: 'ok' if at least one can read this
   * terminal. A tenant runs several dispatchers with independent logins, so
   * one machine's dead session is not the tenant's status.
   */
  status: string
  last_success_at: string | null
  last_failure_at: string | null
  last_failure_reason: string | null
  /** Connected machines for this tenant. Absent on pre-channel-pool servers. */
  machines_total?: number
  /** Of those, how many can actually read this terminal right now. */
  machines_ok?: number
  /** Enrolled and seen recently, connected or not — the denominator for "known". */
  machines_known?: number
  /**
   * This browser's own state, when the caller told the server which machine it
   * is. Null otherwise — the extension does not yet report its own id over the
   * `ping` channel, so today this is always null and the UI works from the
   * rollup alone.
   */
  mine?: { status: string; last_success_at: string | null; last_failure_reason: string | null } | null
}

/**
 * What we know about a tenant-level site key (today: PCC's availability link).
 *
 * The link itself is never returned — it IS the account. All the panel needs
 * is whether one is on file and who put it there.
 */
export interface SiteKeyInfo {
  configured: boolean
  updated_at: string | null
  updated_by_name: string | null
  host: string | null
}

/**
 * Outcome of an on-demand session probe. `probed: false` is a normal answer,
 * not an error — `reason` says why the check couldn't run (extension offline,
 * nothing to query with, called again too soon).
 */
export interface SessionRecheckResult {
  site_id: string
  online: boolean
  probed: boolean
  reason: string | null
  session: ExtensionSessionInfo
}

interface ExtensionLiveStatus {
  /**
   * `true` — backend sees at least one active extension WS for this tenant.
   * `false` — tenant has no active extension (badge should be red).
   * `null` — not yet determined (badge should read "Checking...", never red).
   *   The initial value deliberately starts at `null` so that during the 1-5s
   *   cold-start window (browser restart / backend restart) we never flash
   *   the red "Offline — Reconnecting..." state just because the first poll
   *   lost the race against the extension's WS reconnect.
   */
  online: boolean | null
  sessions: Record<string, ExtensionSessionInfo> | null
  /**
   * Tenant-level credentials keyed by site. Distinct from `sessions`, which is
   * per-machine: a tenant can have a PCC key on file while no machine has yet
   * proved it works, and vice versa after the key is replaced.
   */
  siteKeys: Record<string, SiteKeyInfo> | null
  loading: boolean
  /** Re-read `/extension/status` now instead of waiting for the next poll tick. */
  refresh: () => void
  /**
   * Ask the backend to run a real query against one terminal right now and
   * report back. Used after the user signs in from the "Login" button so the
   * panel reflects reality in seconds rather than on the next 30s poll.
   * Resolves to `null` if the request itself failed.
   */
  recheckSession: (siteId: string) => Promise<SessionRecheckResult | null>
  /** Save this tenant's PCC availability link. Resolves to an error message, or null on success. */
  savePccKey: (url: string) => Promise<string | null>
  /** Remove it — this tenant stops checking PCC. */
  clearPccKey: () => Promise<string | null>
}

interface UseExtensionLiveStatusOptions {
  /** If false, the hook is inert — no HTTP, no socket. */
  enabled?: boolean
  /**
   * Fallback HTTP poll interval in ms. Default 30s. Set to 0 to disable.
   * Socket events are the primary signal for `online`; the poll exists mostly
   * to refresh `sessions` (SSA/ETS/TraPac login freshness) since the backend
   * doesn't push session-level changes, and as a defense if a socket event
   * is missed (e.g. socket reconnect after a transient network drop).
   */
  pollInterval?: number
}

/**
 * Single source of truth for Container Monitor extension status.
 *
 * - `online` is driven primarily by `extension:online` / `extension:offline`
 *   events that `ExtensionGateway` broadcasts on 0↔1 tenant-wide transitions.
 * - `sessions` comes from `GET /api/extension/status` — polled every
 *   `pollInterval` (default 30s) because session freshness (SSA/ETS probe
 *   results) changes slowly and isn't push-worthy.
 * - The initial GET on mount seeds both fields so we don't render a blank
 *   modal while waiting for a socket event that might never fire if nothing
 *   has changed since the extension came online.
 * - HTTP failures do NOT downgrade `online` to `false` — we keep the previous
 *   value (or `null`) so the UI stays in "Checking..." rather than flashing
 *   red on a transient 5xx or a 10s axios timeout during server restart.
 */
export function useExtensionLiveStatus({
  enabled = true,
  pollInterval = 30000,
}: UseExtensionLiveStatusOptions = {}): ExtensionLiveStatus {
  const [online, setOnline] = useState<boolean | null>(null)
  const [sessions, setSessions] = useState<Record<string, ExtensionSessionInfo> | null>(null)
  const [siteKeys, setSiteKeys] = useState<Record<string, SiteKeyInfo> | null>(null)
  const [loading, setLoading] = useState(true)
  // Bumping this re-runs the fetch effect, which is how `refresh()` forces an
  // immediate read without duplicating the request logic.
  const [refreshNonce, setRefreshNonce] = useState(0)

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), [])

  // HTTP fetch — initial seed + periodic fallback for `sessions`.
  useEffect(() => {
    if (!enabled) return
    let cancelled = false

    const fetchStatus = () => {
      api.get('/extension/status')
        .then((resp: { data?: { online?: boolean; sessions?: Record<string, ExtensionSessionInfo>; site_keys?: Record<string, SiteKeyInfo> } }) => {
          if (cancelled) return
          const d = resp.data
          if (d && typeof d.online === 'boolean') setOnline(d.online)
          if (d?.sessions) setSessions(d.sessions)
          if (d?.site_keys) setSiteKeys(d.site_keys)
          setLoading(false)
        })
        .catch(() => {
          if (cancelled) return
          // Keep `online` at whatever it was. Transient errors shouldn't flip to red.
          setLoading(false)
        })
    }

    fetchStatus()

    if (pollInterval > 0) {
      const id = setInterval(fetchStatus, pollInterval)
      return () => {
        cancelled = true
        clearInterval(id)
      }
    }
    return () => {
      cancelled = true
    }
  }, [enabled, pollInterval, refreshNonce])

  // On-demand probe. Merges the returned row straight into `sessions` so the
  // row the user is looking at updates the moment the answer lands, without
  // waiting for the poll to come back around.
  const recheckSession = useCallback(async (siteId: string): Promise<SessionRecheckResult | null> => {
    try {
      const resp = await api.post(`/extension/sessions/${siteId}/recheck`)
      const d = resp.data as SessionRecheckResult | undefined
      if (!d) return null
      if (typeof d.online === 'boolean') setOnline(d.online)
      if (d.session) {
        setSessions((prev) => ({ ...(prev || {}), [siteId]: d.session }))
      }
      return d
    } catch {
      return null
    }
  }, [])

  // Tenant-level PCC link. Both calls return the refreshed key info, so the
  // panel flips to its new state without waiting for the next poll.
  const applyKeyResponse = useCallback((data: SiteKeyInfo | undefined) => {
    if (data) setSiteKeys((prev) => ({ ...(prev || {}), pcc: data }))
  }, [])

  const savePccKey = useCallback(async (url: string): Promise<string | null> => {
    try {
      const resp = await api.put('/extension/sites/pcc/key', { url })
      applyKeyResponse(resp.data as SiteKeyInfo | undefined)
      return null
    } catch (err) {
      // The backend's validation message is the useful part — "that link is
      // missing its access key" tells the user exactly what to fix, where a
      // generic failure would send them back to PCC for a new link.
      const e = err as { response?: { data?: { message?: string } } }
      return e.response?.data?.message || 'Could not save the PCC link.'
    }
  }, [applyKeyResponse])

  const clearPccKey = useCallback(async (): Promise<string | null> => {
    try {
      const resp = await api.delete('/extension/sites/pcc/key')
      applyKeyResponse(resp.data as SiteKeyInfo | undefined)
      return null
    } catch {
      return 'Could not remove the PCC link.'
    }
  }, [applyKeyResponse])

  // Socket.io live updates for presence transitions.
  const socketRef = useRef<ReturnType<typeof io> | null>(null)
  useEffect(() => {
    if (!enabled) return
    const token = localStorage.getItem('token')
    if (!token) return

    const socket = io(
      // Anchored strip — see useExtensionEnrollment.ts. Unanchored, prod's
      // https://api.ealoom.com/api became https:/.ealoom.com and the presence
      // socket never connected.
      import.meta.env.VITE_API_URL?.replace(/\/api\/?$/, '') || 'http://localhost:4000',
      { auth: { token } }
    )
    socketRef.current = socket

    const onOnline = () => setOnline(true)
    const onOffline = () => setOnline(false)
    socket.on('extension:online', onOnline)
    socket.on('extension:offline', onOffline)

    return () => {
      socket.off('extension:online', onOnline)
      socket.off('extension:offline', onOffline)
      socket.disconnect()
      socketRef.current = null
    }
  }, [enabled])

  return { online, sessions, siteKeys, loading, refresh, recheckSession, savePccKey, clearPccKey }
}
